import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Image,
  ActivityIndicator,
  Linking,
} from "react-native";
import { useForm, Controller } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import { router } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import AsyncStorage from "@react-native-async-storage/async-storage";
import i18n, { availableLocales } from "../../locales/i18n";
import customAxios from "../../utils/customAxios";
import secureStorage from "../../utils/secureStorage";
import { authActions } from "../../store/slices/authSlice";
import { appActions, selectIsRTL } from "../../store/slices/appSlice";
import { signInWithGoogleWeb } from "../../utils/googleAuthWeb";

// Import images
import LoginImage from "../../assets/images/sell.png";
import LogoWhite from "../../assets/logo-no-bg-white.png";

const WebLoginScreen: React.FC = () => {
  const dispatch = useDispatch();
  const [showLanguages, setShowLanguages] = useState(false);
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleSpinner, setGoogleSpinner] = useState(false);

  // Use Redux selector for locale and RTL - single source of truth (reactive)
  const locale = useSelector((state: any) => state.app.locale);
  const isRTL = useSelector(selectIsRTL);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      gmail: "",
      Password: "",
    },
  });

  const handleSignIn = async (data: any) => {
    setButtonSpinner(true);
    try {
      const response = await customAxios.post("/mobile/login", {
        login: data.gmail,
        password: data.Password,
      });

      await AsyncStorage.setItem("accessToken", response.data?.accessToken);
      await secureStorage.setItemAsync(
        "refreshToken",
        response.data.refreshToken,
      );

      try {
        const userDetailsResponse = await customAxios.get("/users/check");
        const userInfo = userDetailsResponse.data?.userinfo;

        // Block superadmin from using the web app view
        if (userInfo?.role === "superadmin") {
          await AsyncStorage.removeItem("accessToken");
          await secureStorage.deleteItemAsync("refreshToken");
          setButtonSpinner(false);
          Toast.show(i18n.t("adminRedirectMessage"), {
            type: "warning",
            duration: 8000,
          });
          // Open admin panel in new tab on web
          Linking.openURL("https://khdame.com/admin/login");
          return;
        }

        dispatch(authActions.setUser(userInfo));

        if (userInfo?.is_verified === false) {
          Toast.show(i18n.t("verifyEmailReminder"), {
            type: "info",
            duration: 6000,
          });
        }

        Toast.show(i18n.t("loginSuccess"), { type: "success" });
        setButtonSpinner(false);
        router.replace("/(drawer)/start");
      } catch (userError) {
        Toast.show(i18n.t("errorOccurred"), { type: "danger" });
        setButtonSpinner(false);
      }
    } catch (error: any) {
      setButtonSpinner(false);
      Toast.show(error?.response?.data?.error || i18n.t("errorOccurred"), {
        type: "danger",
      });
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleSpinner(true);
    try {
      const { idToken } = await signInWithGoogleWeb();

      const response = await customAxios.post("/auth/google", { idToken });

      await AsyncStorage.setItem("accessToken", response.data?.accessToken);
      await secureStorage.setItemAsync(
        "refreshToken",
        response.data.refreshToken,
      );

      const userDetailsResponse = await customAxios.get("/users/check");
      const userInfo = userDetailsResponse.data?.userinfo;

      if (userInfo?.role === "superadmin") {
        await AsyncStorage.removeItem("accessToken");
        await secureStorage.deleteItemAsync("refreshToken");
        setGoogleSpinner(false);
        Toast.show(i18n.t("adminRedirectMessage"), {
          type: "warning",
          duration: 8000,
        });
        Linking.openURL("https://khdame.com/admin/login");
        return;
      }

      dispatch(authActions.setUser(userInfo));
      Toast.show(i18n.t("loginSuccess"), { type: "success" });
      setGoogleSpinner(false);
      router.replace("/(drawer)/start");
    } catch (error: any) {
      setGoogleSpinner(false);
      if (error?.message === "cancelled") return;
      Toast.show(error?.response?.data?.error || i18n.t("googleSignInError"), {
        type: "danger",
      });
    }
  };

  const handleLanguageChange = async (languageCode: string) => {
    setShowLanguages(false);
    i18n.locale = languageCode;
    dispatch(appActions.setLocale(languageCode));
    // Save to localStorage for sync read on next load
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem("appLocale", languageCode);
    }
    await AsyncStorage.setItem("appLocale", languageCode);
    // No reload needed - Redux state change triggers re-render
  };

  return (
    <View
      style={[
        styles.container,
        { flexDirection: isRTL ? "row-reverse" : "row" },
      ]}
    >
      {/* Left Panel - Branding */}
      <View style={styles.leftPanel}>
        <View style={styles.brandingContent}>
          <Image source={LogoWhite} style={styles.logo} resizeMode="contain" />
          <Text style={styles.brandTitle}>خدام</Text>
          <Text style={styles.brandSubtitle}>{i18n.t("welcomeIntoDesc1")}</Text>
          <Image
            source={LoginImage}
            style={styles.illustrationImage}
            resizeMode="contain"
          />
        </View>
      </View>

      {/* Right Panel - Login Form */}
      <View style={styles.rightPanel}>
        <View style={styles.formContainer}>
          {/* Language Selector */}
          <View style={styles.languageSelector}>
            <TouchableOpacity
              style={styles.languageButton}
              onPress={() => setShowLanguages(!showLanguages)}
            >
              <Text style={styles.languageButtonText}>🌐</Text>
              <Text style={styles.languageButtonLabel}>
                {availableLocales.find((l) => l.code === locale)?.name ||
                  locale}
              </Text>
            </TouchableOpacity>

            {showLanguages && (
              <View style={styles.languageDropdown}>
                {availableLocales.map((lang) => (
                  <TouchableOpacity
                    key={lang.code}
                    style={[
                      styles.languageOption,
                      locale === lang.code && styles.languageOptionActive,
                    ]}
                    onPress={() => handleLanguageChange(lang.code)}
                  >
                    <Text
                      style={[
                        styles.languageOptionText,
                        locale === lang.code && styles.languageOptionTextActive,
                      ]}
                    >
                      {lang.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>

          {/* Header */}
          <View style={styles.formHeader}>
            <Text style={styles.formTitle}>{i18n.t("loginTitle")}</Text>
            <Text style={styles.formSubtitle}>
              {i18n.t("loginDesc") ||
                "قم بتسجيل الدخول إلى حسابك الموجود على خدام"}
            </Text>
          </View>

          {/* Email/Username Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>{i18n.t("usernameOrEmail")}</Text>
            <View style={styles.inputWrapper}>
              <Feather
                name="user"
                size={20}
                color="#999"
                style={styles.inputIcon}
              />
              <Controller
                control={control}
                name="gmail"
                rules={{ required: i18n.t("usernameOrEmailRequired") }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.textInput}
                    placeholder={
                      i18n.t("enterUsernameOrEmail") ||
                      "أدخل اسم المستخدم أو البريد الإلكتروني"
                    }
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType="email-address"
                  />
                )}
              />
            </View>
            {errors.gmail && (
              <Text style={styles.errorText}>{errors.gmail.message}</Text>
            )}
          </View>

          {/* Password Field */}
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>{i18n.t("password")}</Text>
            <View style={styles.inputWrapper}>
              <Feather
                name="lock"
                size={20}
                color="#999"
                style={styles.inputIcon}
              />
              <Controller
                control={control}
                name="Password"
                rules={{ required: i18n.t("passwordRequired") }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    style={styles.textInput}
                    placeholder="••••••••"
                    placeholderTextColor="#999"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    secureTextEntry={!showPassword}
                  />
                )}
              />
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                <Feather
                  name={showPassword ? "eye" : "eye-off"}
                  size={20}
                  color="#999"
                />
              </TouchableOpacity>
            </View>
            {errors.Password && (
              <Text style={styles.errorText}>{errors.Password.message}</Text>
            )}
          </View>

          {/* Forgot Password Link */}
          <TouchableOpacity
            style={styles.forgotPassword}
            onPress={() => router.push("/(routes)/(auth)/forget-password")}
          >
            <Text style={styles.forgotPasswordText}>
              {i18n.t("forgotPassword")}
            </Text>
          </TouchableOpacity>

          {/* Login Button */}
          <TouchableOpacity
            style={styles.loginButton}
            onPress={handleSubmit(handleSignIn)}
            disabled={buttonSpinner}
          >
            {buttonSpinner ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.loginButtonText}>{i18n.t("loginTitle")}</Text>
            )}
          </TouchableOpacity>

          {/* Divider */}
          <View style={styles.dividerContainer}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>{i18n.t("orContinueWith")}</Text>
            <View style={styles.dividerLine} />
          </View>

          {/* Google Sign-In Button */}
          <TouchableOpacity
            style={styles.googleButton}
            onPress={handleGoogleSignIn}
            disabled={googleSpinner}
          >
            {googleSpinner ? (
              <ActivityIndicator size="small" color="#004E6D" />
            ) : (
              <>
                <Image
                  source={{
                    uri: "https://developers.google.com/identity/images/g-logo.png",
                  }}
                  style={styles.googleIcon}
                  resizeMode="contain"
                />
                <Text style={styles.googleButtonText}>
                  {i18n.t("googleSignIn")}
                </Text>
              </>
            )}
          </TouchableOpacity>

          {/* Sign Up Link */}
          <View style={styles.signupLinkContainer}>
            <Text style={styles.signupText}>{i18n.t("donthaveaccount")}</Text>
            <TouchableOpacity
              onPress={() => router.push("/(routes)/(auth)/signup")}
            >
              <Text style={styles.signupLink}>{i18n.t("createAccount")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    minHeight: "100vh" as any,
    backgroundColor: "#F8F9FA",
  },
  leftPanel: {
    flex: 1,
    backgroundColor: "#004E6D",
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  brandingContent: {
    alignItems: "center",
    maxWidth: 400,
  },
  logo: {
    width: 80,
    height: 80,
    marginBottom: 24,
  },
  brandTitle: {
    fontSize: 56,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 16,
  },
  brandSubtitle: {
    fontSize: 20,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    lineHeight: 28,
    marginBottom: 40,
  },
  illustrationImage: {
    width: 300,
    height: 250,
  },
  rightPanel: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
    backgroundColor: "#FFFFFF",
  },
  formContainer: {
    width: "100%",
    maxWidth: 420,
  },
  languageSelector: {
    position: "absolute" as any,
    top: -60,
    right: 0,
    zIndex: 100,
  },
  languageButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F0F0F0",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 8,
  },
  languageButtonText: {
    fontSize: 20,
  },
  languageButtonLabel: {
    fontSize: 16,
    color: "#333",
    fontWeight: "500",
  },
  languageDropdown: {
    position: "absolute" as any,
    top: 44,
    right: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 5,
    minWidth: 140,
    overflow: "hidden",
    zIndex: 9999,
  },
  languageOption: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  languageOptionActive: {
    backgroundColor: "#E8F4F8",
  },
  languageOptionText: {
    fontSize: 16,
    color: "#333",
  },
  languageOptionTextActive: {
    color: "#004E6D",
    fontWeight: "600",
  },
  formHeader: {
    marginBottom: 32,
  },
  formTitle: {
    fontSize: 36,
    fontWeight: "bold",
    color: "#004E6D",
    marginBottom: 8,
    textAlign: "center",
  },
  formSubtitle: {
    fontSize: 18,
    color: "#666",
    textAlign: "center",
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333",
    marginBottom: 8,
    textAlign: "right",
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8F9FA",
    borderWidth: 1,
    borderColor: "#E0E0E0",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 52,
  },
  inputIcon: {
    marginRight: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 18,
    color: "#333",
    textAlign: "right",
    outlineStyle: "none" as any,
  },
  errorText: {
    color: "#E53935",
    fontSize: 14,
    marginTop: 4,
    textAlign: "right",
  },
  forgotPassword: {
    alignSelf: "flex-end",
    marginBottom: 24,
  },
  forgotPasswordText: {
    fontSize: 16,
    color: "#0596D0",
    fontWeight: "500",
  },
  loginButton: {
    backgroundColor: "#004E6D",
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
    marginBottom: 24,
    shadowColor: "#004E6D",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  loginButtonText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "600",
  },
  signupLinkContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
  },
  signupText: {
    fontSize: 16,
    color: "#666",
  },
  signupLink: {
    fontSize: 16,
    color: "#004E6D",
    fontWeight: "600",
  },
  dividerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: "#E0E0E0",
  },
  dividerText: {
    marginHorizontal: 12,
    color: "#999",
    fontSize: 14,
  },
  googleButton: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    backgroundColor: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    gap: 10,
    marginBottom: 24,
  },
  googleIcon: {
    width: 20,
    height: 20,
  },
  googleButtonText: {
    color: "#333",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default WebLoginScreen;
